import React, { useState } from "react";
import CSS from "./Login.module.css";
import TermsOverlay from "../../components/TermsConditions/TermsOverlay";
import { Autocomplete, Link, TextField } from "@mui/material";
import { JOIN_OUR_WAITLIST } from "../../constants/constants";
import { useFetchColleges } from "../../hooks/FetchColleges";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Loading from "../../components/Loading/Loading";
import LoginButton from "../../components/Login/LoginButton";
import PasswordlessEmailLogin from "../../components/Login/PasswordlessEmailLogin";
import { styled } from "@mui/system";
import SearchIcon from "@mui/icons-material/Search";
import { motion } from "framer-motion";
import LogoDot from "../../components/LogoDot/LogoDot";
import { useNavigate } from "react-router-dom";
import AnimatedDisclaimer from "./components/AnimatedDisclaimer";

const CollegeSearch = styled(Autocomplete)(({ theme }) => ({
  width: 320,
  "& .MuiOutlinedInput-root": {
    borderRadius: 999,
    paddingRight: theme.spacing(1.5),
    backgroundColor:
      theme.palette.mode === "dark" ? "#020617" : "#F9FAFB",
    boxShadow: "0 10px 30px rgba(15, 23, 42, 0.35)",
    "& fieldset": {
      borderColor: "rgba(148, 163, 184, 0.6)",
    },
    "&:hover fieldset": {
      borderColor: "#7BB7FF",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#58A6FF",
      boxShadow: "0 0 0 1px rgba(88, 166, 255, 0.7)",
    },
  },
  "& .MuiInputBase-input": {
    padding: theme.spacing(1.2, 0),
    fontSize: "0.95rem",
  },
  "& .MuiAutocomplete-popupIndicator": {
    display: "none",
  },

  // 👇 dropdown (paper + listbox + options)
  "& .MuiAutocomplete-paper": {
    marginTop: theme.spacing(1),
    borderRadius: 16,
    backgroundColor: "rgba(15, 23, 42, 0.98)", // deep navy, not flat black
    color: "#F9FAFB",
    boxShadow: "0 18px 45px rgba(15,23,42,0.9)",
    border: "1px solid rgba(148,163,184,0.6)",
  },
  "& .MuiAutocomplete-listbox": {
    padding: theme.spacing(0.5, 0),
    "& .MuiAutocomplete-option": {
      padding: theme.spacing(1.25, 2),
      fontSize: "0.95rem",
      "&[aria-selected='true']": {
        backgroundColor: "rgba(88,166,255,0.22)", // selected
      },
      "&.Mui-focused": {
        backgroundColor: "rgba(88,166,255,0.14)", // hover/focus
      },
    },
  },
}));

const Tagline = styled(motion.p)(({ theme }) => ({
  margin: 0,
  marginTop: theme.spacing(1.5),
  marginBottom: theme.spacing(1),
  fontSize: "0.95rem", // smaller than logo
  color: "rgba(148, 163, 184, 0.95)",
  fontFamily: "'Chalkboard SE', 'Comic Sans MS', system-ui",
  letterSpacing: "-0.01em",
}));


const providerIdMapping = {
  bbdefa2950f49882: "google",
  "9fbf261b62c1d7c0": "microsoft",
  "4815162342": "email",
};

const loadingSpinner = (
  <Box
    sx={{
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
    }}
  >
    <Loading />
  </Box>
);

const LogoText = styled(Typography)(({ theme }) => ({
  fontSize: "1.7rem",
  fontWeight: 800,
  fontFamily: "'Chalkboard SE', 'Comic Sans MS', system-ui",
  letterSpacing: "-0.03em",
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.8),
}));

const Login = () => {
  const [selectedCollege, setSelectedCollege] = useState(null);
  const [
    supportedColleges,
    supportedCollegesFetchLoading,
    supportedCollegesFetchError,
  ] = useFetchColleges();

  const getLoginFromProviderId = (providerId) => {
    const providerName = providerIdMapping[providerId];
    return providerName;
  };

  const navigate = useNavigate();

  const JoinWaitListLink = () => {
    return (
      <Link
        rel="noopener"
        variant="body2"
        underline="none"
        href={JOIN_OUR_WAITLIST}
        target="_blank"
      >
        Not found? join our waitlist!
      </Link>
    );
  };

  const handleOnChange = (event, newValue) => {
    localStorage.setItem("selectedSchoolId", newValue.id);
    localStorage.setItem("selectedSchoolName", newValue.name);
    setSelectedCollege(newValue);
  };

  const renderSignInInfo = () => {
    if (!selectedCollege) return null;

    return (
      <div className={CSS.signinInfo}>
        <AnimatedDisclaimer />
        <div className={CSS.loginButton}>
        {getLoginFromProviderId(selectedCollege.providerId) === "email" ? (
          <PasswordlessEmailLogin
            selectedCollege={selectedCollege}
            onSuccess={() => navigate("/post-login", { replace: true })}
          />
        ) : (
          <LoginButton provider={getLoginFromProviderId(selectedCollege.providerId)} />
        )}
        </div>
        <span className={CSS.termsOverlay}>
          <TermsOverlay />
        </span>
      </div>
    );
  };

  return (
    <div className={CSS.loginContainer}>
      {!supportedCollegesFetchError &&
        supportedCollegesFetchLoading &&
        loadingSpinner}
      {!supportedCollegesFetchError && !supportedCollegesFetchLoading && (
        <div className={CSS.centeredContainer}>
          <LogoText style={{ color: "#3F00FF", textDecoration: "none" }}>
            <LogoDot
              animate={{
                scale: [1, 1.08, 1],
                boxShadow: [
                  "0 0 0 0 rgba(88, 166, 255, 0.45)",
                  "0 0 0 6px rgba(88, 166, 255, 0.0)", // halo expands + fades
                  "0 0 0 0 rgba(88, 166, 255, 0.0)",
                ],
              }}
              transition={{
                duration: 1.6,
                repeat: Infinity,
                repeatType: "loop",
                ease: "easeOut",
              }}
            /> 
            VibeU
          </LogoText>

          <Tagline
            initial={{ y: 6, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ type: "spring", stiffness: 100, damping: 18, delay: 0.70 }}
          >
            Login, Match, Vibe
          </Tagline>
          <div className={CSS.collegeOptions}>
          <CollegeSearch
              onChange={handleOnChange}
              options={supportedColleges || []}
              getOptionLabel={(college) => college.name || ""}
              noOptionsText={<JoinWaitListLink />}
              componentsProps={{
                paper: {
                  sx: {
                    mt: 1,
                    borderRadius: 2,
                    bgcolor: "rgba(15, 23, 42, 0.98)",        // deep navy, not flat black
                    color: "#F9FAFB",
                    boxShadow: "0 18px 45px rgba(15,23,42,0.9)",
                    border: "1px solid rgba(148,163,184,0.6)",
                  },
                },
                popper: {
                  sx: {
                    zIndex: 1500, // optional, keeps it above card if needed
                  },
                },
              }}
              ListboxProps={{
                sx: {
                  py: 0.5,
                  "& .MuiAutocomplete-option": {
                    py: 1.25,
                    px: 2,
                    fontSize: "0.95rem",
                    "&[aria-selected='true']": {
                      bgcolor: "rgba(88,166,255,0.22)",      // selected
                    },
                    "&.Mui-focused": {
                      bgcolor: "rgba(88,166,255,0.14)",      // hover/focus
                    },
                  },
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Find your school..."
                  variant="outlined"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <SearchIcon
                          sx={{ mr: 1, ml: 1, fontSize: 20, opacity: 0.7 }}
                        />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </div>
          <div>{renderSignInInfo()}</div>
        </div>
      )}
      {supportedCollegesFetchError && (
        <Typography variant="body1" color="white">
          An error occurred while fetching colleges
        </Typography>
      )}
    </div>
  );
};

export default Login;
